import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'rsuite';
import { has, set, get, split } from 'lodash/fp';
import Pagination from '../../Pagination';
import DateCell from '../../widgets/custom-cell/Date';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import ActionCell from './ActionCell';
import GuestNameCell from './GuestNameCell';
import StatusCell from '../../reservations/List/StatusCell';
import PaginationType from '../../../interfaces/PaginationType';
import NotifyGuestConfirmationModal from '../../reservations/NotifyGuestConfirmationModal';
import CancelReservationConfirmationModal from '../../reservations/CancelReservationConfirmationModal';

const { Column, HeaderCell } = Table;

const ReservationsWithOnholdItemList = ({
  items,
  isFetching,
  isSaving,
  onInit,
  onReset,
  query,
  loadedPages,
  pagination,
  onChangePage,
  onChangeLimit,
  onSort,
  onCancel,
  onCanceled,
  isSaved,
  isNotifyGuestEnabled,
  hasActiveSmtpAccounts
}) => {
  const [showNotifyGuestDialog, setShowNotifyGuestDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [emailInput, setEmailInput] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cancelRequestInFlight = useRef(false);
  const prevIsSaved = useRef(isSaved);
  const isCanceling = isSaving || isSubmitting;
  const [sortColumn, sortType] = split(':', get('order', query));

  useEffect(() => {
    onInit(query);
    return onReset;
  }, []);

  useEffect(
    () => {
      if (!prevIsSaved.current && isSaved) {
        cancelRequestInFlight.current = false;
        setShowNotifyGuestDialog(false);
        setShowCancelDialog(false);
        setCancellationReason('');
        setSelectedItem(null);
        setEmailInput(null);
        setIsSubmitting(false);
        onCanceled();
      }
      prevIsSaved.current = isSaved;
    },
    [isSaved]
  );

  const handleSortColumn = (column, type) => {
    onSort(column, type, query);
  };

  const handleChangePage = page => {
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  const handleChangeLimit = limit => {
    onChangeLimit({ ...query, limit, page: 1 });
  };

  const handleOnCancel = reservation => {
    if (isCanceling) return;
    setShowCancelDialog(true);
    setSelectedItem(reservation);
  };

  const handleConfirmCancelDialog = reason => {
    if (isCanceling || cancelRequestInFlight.current) return;

    if (isNotifyGuestEnabled && hasActiveSmtpAccounts) {
      setShowCancelDialog(false);
      setShowNotifyGuestDialog(true);
      setCancellationReason(reason);
      return;
    }

    cancelRequestInFlight.current = true;
    setIsSubmitting(true);
    onCancel(selectedItem.id, false, null, reason);
  };

  const handleCloseCancelDialog = () => {
    if (isCanceling) return;
    setShowCancelDialog(false);
    setCancellationReason('');
    setSelectedItem(null);
  };

  const updateStatus = (notifyGuest = false) => {
    if (isCanceling || cancelRequestInFlight.current) return;
    cancelRequestInFlight.current = true;
    setIsSubmitting(true);
    onCancel(selectedItem.id, notifyGuest, emailInput, cancellationReason);
  };

  const handleCancelNotifyGuestDialog = () => {
    if (isCanceling) return;
    setShowNotifyGuestDialog(false);
    setCancellationReason('');
    setSelectedItem(null);
  };

  return (
    <Fragment>
      <Table
        height={360}
        loading={isFetching || isCanceling}
        data={items}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={handleSortColumn}
        bordered
      >
        <Column width={120} sortable resizable>
          <CustomHeaderCell>Reservation ID</CustomHeaderCell>
          <Table.Cell dataKey="id" />
        </Column>
        <Column width={220} sortable resizable>
          <CustomHeaderCell>Guest Name</CustomHeaderCell>
          <GuestNameCell dataKey="lastName" />
        </Column>
        <Column width={160} sortable resizable>
          <CustomHeaderCell>Date/Time</CustomHeaderCell>
          <DateCell dataKey="requestTime" format="MM/DD/YYYY hh:mm A" />
        </Column>
        <Column width={120} sortable resizable>
          <CustomHeaderCell>Status</CustomHeaderCell>
          <StatusCell dataKey="status" />
        </Column>
        <Column width={120} align="right" sortable resizable>
          <CustomHeaderCell>Onhold Qty</CustomHeaderCell>
          <Table.Cell dataKey="heldQuantity" />
        </Column>
        <Column width={150} align="center" resizable>
          <HeaderCell>Actions</HeaderCell>
          <ActionCell
            dataKey="id"
            isSaving={isCanceling}
            savingId={get('id', selectedItem)}
            handleCancel={handleOnCancel}
          />
        </Column>
      </Table>

      <Pagination
        page={pagination.currentPage}
        limit={pagination.limit}
        total={pagination.totalCount}
        onChangePage={handleChangePage}
        onChangeLimit={handleChangeLimit}
      />
      {hasActiveSmtpAccounts && (
        <NotifyGuestConfirmationModal
          show={showNotifyGuestDialog}
          email={
            get('guest.email', selectedItem) || get('email', selectedItem) || ''
          }
          showEmailField
          isSaving={isCanceling}
          onConfirm={() => updateStatus(true)}
          onSkip={() => updateStatus(false)}
          onCancel={handleCancelNotifyGuestDialog}
          onChangeEmail={setEmailInput}
        />
      )}
      <CancelReservationConfirmationModal
        show={showCancelDialog}
        isSaving={isCanceling}
        onCancel={handleCloseCancelDialog}
        onConfirm={handleConfirmCancelDialog}
      />
    </Fragment>
  );
};

ReservationsWithOnholdItemList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFetching: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  onInit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCanceled: PropTypes.func,
  isSaved: PropTypes.bool,
  isNotifyGuestEnabled: PropTypes.bool,
  hasActiveSmtpAccounts: PropTypes.bool
};

ReservationsWithOnholdItemList.defaultProps = {
  isSaving: false,
  isSaved: false,
  onCanceled: () => {},
  isNotifyGuestEnabled: false,
  hasActiveSmtpAccounts: false
};

export default ReservationsWithOnholdItemList;
